import classNames from "classnames";
import Loader from "components/DesignSystem/Loader/loader";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export type PdfPageClickHandler = (
  x: number,
  y: number,
  pageNumber: number
) => void;

export type PdfScrollTarget = {
  pageNumber: number;
  y: number; // percentage 0-100 within the page
};

type PdfViewerProps = {
  url: string;
  containerWidth: number;
  scrollTo?: PdfScrollTarget | null;
  pageOverlay?: (pageNumber: number) => React.ReactNode;
  onPageClick?: PdfPageClickHandler;
  /** Shrink to the rendered pages instead of filling the parent, still capped at its height. */
  fitContent?: boolean;
};

export const PdfViewer = ({
  url,
  containerWidth,
  scrollTo,
  pageOverlay,
  onPageClick,
  fitContent = false,
}: PdfViewerProps) => {
  const [numPages, setNumPages] = useState(0);
  const [firstPageRendered, setFirstPageRendered] = useState(false);
  const generationRef = useRef(0);
  const isDocLoading = !firstPageRendered;

  const prevUrlRef = useRef(url);
  if (prevUrlRef.current !== url) {
    prevUrlRef.current = url;
    generationRef.current += 1;
    setNumPages(0);
    setFirstPageRendered(false);
  }
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollToRef = useRef(scrollTo);
  scrollToRef.current = scrollTo;
  const pageElsRef = useRef<Record<number, HTMLDivElement | null>>({});

  const scrollToPage = (pageEl: HTMLDivElement, y: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const containerTop = container.getBoundingClientRect().top;
    const pageTop = pageEl.getBoundingClientRect().top;
    const pageHeight = pageEl.getBoundingClientRect().height;
    const scrollOffset =
      container.scrollTop +
      (pageTop - containerTop) +
      (y / 100) * pageHeight -
      container.clientHeight / 2;
    container.scrollTo({ top: scrollOffset, behavior: "smooth" });
  };

  useEffect(() => {
    if (!scrollTo) return;
    const pageEl = pageElsRef.current[scrollTo.pageNumber];
    if (pageEl) scrollToPage(pageEl, scrollTo.y);
  }, [scrollTo]);

  const handlePageRef = (pageNumber: number) => (el: HTMLDivElement | null) => {
    pageElsRef.current[pageNumber] = el;
    if (!el) return;
    const target = scrollToRef.current;
    if (target && target.pageNumber === pageNumber) {
      scrollToPage(el, target.y);
    }
  };

  const handlePageClick =
    (pageNumber: number) => (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onPageClick) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      onPageClick(x, y, pageNumber);
    };

  const pageWidth = containerWidth > 0 ? containerWidth - 32 : undefined;
  const currentGeneration = generationRef.current;

  return (
    <div
      className={classNames("t-relative t-w-full", {
        "t-h-full": !fitContent,
        "t-flex t-max-h-full t-flex-col": fitContent,
        "t-min-h-60": fitContent && isDocLoading,
      })}
    >
      {isDocLoading && (
        <div className="t-absolute t-inset-0 t-flex t-items-center t-justify-center t-bg-neutral-0 t-z-20">
          <Loader />
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className={classNames(
          "t-overflow-auto t-w-full t-bg-neutral-0 t-flex t-flex-col t-items-center t-gap-4 t-py-4",
          { "t-h-full": !fitContent, "t-min-h-0 t-flex-1": fitContent }
        )}
      >
        <Document
          file={url}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          error={
            <div className="t-text-body-sm t-text-text-30 t-mt-8">
              Failed to load PDF.
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;

            return (
              <div
                key={pageNumber}
                ref={handlePageRef(pageNumber)}
                className="t-relative t-shadow-light-30"
                onClick={handlePageClick(pageNumber)}
                style={{ cursor: onPageClick ? "crosshair" : "default" }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  renderTextLayer
                  renderAnnotationLayer
                  onRenderSuccess={
                    pageNumber === 1
                      ? () => {
                          if (generationRef.current !== currentGeneration)
                            return;
                          setFirstPageRendered(true);
                        }
                      : undefined
                  }
                />
                {pageOverlay?.(pageNumber)}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
};
